import { X, Circle, Pin } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { cn } from "../lib/utils";
import {
  edgeScrollDelta,
  insertionIndicatorX,
  insertTabIdAt,
  isSameOrder,
  resolveInsertionIndex,
  shiftTabId,
  type TabBounds,
} from "../lib/tabOrder";
import type { EditorTab, EditorTabsProps } from "../types";

/** Horizontal scrollbar: explicit WebKit + Firefox colors so the thumb stays
 *  visible whenever tabs overflow (overlay scrollbars often hide until hover). */
const TAB_STRIP_SCROLLBAR =
  "[scrollbar-width:thin] [scrollbar-color:hsl(var(--muted-foreground)/0.55)_hsl(var(--muted)/0.35)] " +
  "[&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-muted/40 " +
  "[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:min-h-[6px] [&::-webkit-scrollbar-thumb]:bg-muted-foreground/50 " +
  "[&::-webkit-scrollbar-thumb]:hover:bg-muted-foreground/65";

/**
 * Private MIME type carried by a tab drag.
 *
 * Reordering only reacts to drags that set it, so dragging a file from the desktop
 * or selected text from the editor over the strip is ignored instead of shuffling
 * the user's tabs.
 */
const TAB_DRAG_MIME = "application/x-ncoder-editor-tab";

/** Width of the insertion bar, in px. Kept in one place: the geometry that centres
 *  it on a seam and the class that draws it have to agree. */
const INDICATOR_WIDTH = 2;

/**
 * EditorTabs — Tab bar for multi-file editing.
 *
 * Fully controlled: the parent owns tab state, including ORDER. Supports preview
 * tabs (italic), pinned tabs, dirty indicators, and drag-to-reorder.
 *
 * THE DRAG MODEL
 * --------------
 * The STRIP is the drop target, not each tab, and a drag in progress is one
 * number: `insertionIndex`, the gap the tab would land in (see `lib/tabOrder`).
 * Three consequences, and each of them is load-bearing:
 *
 *  - Each gap has exactly one name, so crossing a boundary changes the state once
 *    and pointer jitter on a midpoint changes nothing. The earlier
 *    `(tab, before | after)` encoding named every gap twice, a few pixels apart,
 *    and flickered between the two spellings.
 *  - The indicator is ONE element owned by the strip. It moves; it is never
 *    unmounted in one tab and recreated in the next.
 *  - The index that draws the indicator is the index the drop applies, so the
 *    preview cannot promise a position the drop will not deliver.
 *
 * Native HTML5 DnD is retained deliberately: it provides the drag image, the
 * suppressed click after a drag (so dragging a tab never activates it), and Esc
 * cancellation via `dragend` — with no dependency. Its coarse `dragover` sampling
 * does not matter here because the indicator snaps to gaps rather than following
 * the pointer, and gaps only change at midpoints.
 *
 * Both `dragenter` and `dragover` are cancelled: HTML5 DnD treats an element as a
 * valid drop target only while each is, and cancelling `dragover` alone gets one
 * event through before the browser starts rejecting the element.
 */
export function EditorTabs({
  tabs,
  activeTabId,
  onTabClick,
  onTabClose,
  onTabDoubleClick,
  onTabContextMenu,
  onTabReorder,
}: EditorTabsProps) {
  const stripRef = useRef<HTMLDivElement>(null);
  /** The scrolled content. Indicator coordinates are relative to this. */
  const rowRef = useRef<HTMLDivElement>(null);
  const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(false);

  const [draggingTabId, setDraggingTabId] = useState<string | null>(null);
  const [insertionIndex, setInsertionIndex] = useState<number | null>(null);
  const [indicatorX, setIndicatorX] = useState<number | null>(null);

  const canReorder = Boolean(onTabReorder) && tabs.length > 1;

  // Last pointer position from a real drag event. The auto-scroll frame loop
  // reads it; nothing else does, and no listener outside this component writes it.
  const pointerXRef = useRef<number | null>(null);
  const autoScrollFrameRef = useRef<number | null>(null);
  const draggingTabIdRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    const el = stripRef.current;
    if (!el) return;
    const measure = () => {
      setHasHorizontalOverflow(el.scrollWidth > el.clientWidth + 1);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [tabs, activeTabId]);

  // Keep the active tab reachable when the strip overflows: activating a tab from
  // a keyboard or from outside the strip (quick-open, a tree click) must not leave
  // it scrolled out of sight. Scoped to the strip's own scroll box, so it never
  // scrolls the page.
  useEffect(() => {
    if (!activeTabId) return;
    const tabEl = stripRef.current?.querySelector<HTMLElement>(
      `[data-tab-id="${cssEscape(activeTabId)}"]`,
    );
    tabEl?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [activeTabId]);

  /**
   * Measure the tabs in the scrolled content's coordinate space.
   *
   * Live rects, read at the moment they are needed rather than cached at drag
   * start: the strip can scroll (and does, while auto-scrolling) mid-drag, and a
   * cached measurement would put the indicator on the wrong gap the instant it
   * did. Bounded by the number of open tabs, which is a handful.
   */
  const measureTabs = useCallback((): {
    bounds: TabBounds[];
    rowLeft: number;
    contentWidth: number;
  } | null => {
    const row = rowRef.current;
    if (!row) return null;
    const rowLeft = row.getBoundingClientRect().left;
    const bounds: TabBounds[] = [];
    for (const el of row.querySelectorAll<HTMLElement>("[data-tab-id]")) {
      const rect = el.getBoundingClientRect();
      bounds.push({
        id: el.dataset.tabId as string,
        left: rect.left - rowLeft,
        right: rect.right - rowLeft,
      });
    }
    return { bounds, rowLeft, contentWidth: row.scrollWidth };
  }, []);

  /** Recompute the gap (and the bar's position) for a viewport-space pointer x. */
  const updateInsertion = useCallback(
    (clientX: number) => {
      const dragged = draggingTabIdRef.current;
      if (!dragged) return;
      const measured = measureTabs();
      if (!measured) return;

      const contentX = clientX - measured.rowLeft;
      const index = resolveInsertionIndex(measured.bounds, dragged, contentX);
      const seam = insertionIndicatorX(measured.bounds, dragged, index);
      // Centre the bar on the seam (`left = seam - half its width`), then keep it
      // inside the content box so the first and last gaps show a full-width bar
      // instead of one half-clipped by the strip's edge.
      const x =
        seam === null
          ? null
          : Math.max(
              0,
              Math.min(seam - INDICATOR_WIDTH / 2, measured.contentWidth - INDICATOR_WIDTH),
            );

      setInsertionIndex((current) => (current === index ? current : index));
      setIndicatorX((current) => (current === x ? current : x));
    },
    [measureTabs],
  );

  const stopAutoScroll = useCallback(() => {
    if (autoScrollFrameRef.current !== null) {
      cancelAnimationFrame(autoScrollFrameRef.current);
      autoScrollFrameRef.current = null;
    }
  }, []);

  /**
   * Scroll the strip while the pointer rests near either end, so a tab can be
   * dragged to a position that is not currently on screen.
   *
   * Chromium does not auto-scroll this container during a native drag (measured),
   * and without this the indicator freezes at the last visible gap while the
   * pointer keeps moving — which reads as the drag having broken.
   *
   * A frame loop, not a timer: it is bound to the drag's lifetime (started on
   * dragstart, cancelled on dragend / drop / unmount), it moves in step with
   * painting rather than at some invented interval, and it reads only the pointer
   * position that real drag events recorded.
   */
  const startAutoScroll = useCallback(() => {
    if (autoScrollFrameRef.current !== null) return;

    const step = () => {
      autoScrollFrameRef.current = null;
      const strip = stripRef.current;
      const pointerX = pointerXRef.current;
      if (!strip || pointerX === null || !draggingTabIdRef.current) return;

      const rect = strip.getBoundingClientRect();
      const delta = edgeScrollDelta(
        { left: rect.left, width: rect.width },
        {
          scrollLeft: strip.scrollLeft,
          scrollWidth: strip.scrollWidth,
          clientWidth: strip.clientWidth,
        },
        pointerX,
      );

      if (delta !== 0) {
        strip.scrollLeft += delta;
        // The content moved under a stationary pointer, so the gap it points at
        // has changed: recompute from the same pointer position.
        updateInsertion(pointerX);
      }

      // Keep the loop alive for as long as the drag is: the pointer may sit still
      // inside the edge zone, and Chromium delivers no further drag events then.
      autoScrollFrameRef.current = requestAnimationFrame(step);
    };

    autoScrollFrameRef.current = requestAnimationFrame(step);
  }, [updateInsertion]);

  /** One place that ends a drag, whatever ended it. */
  const endDrag = useCallback(() => {
    draggingTabIdRef.current = null;
    pointerXRef.current = null;
    stopAutoScroll();
    setDraggingTabId(null);
    setInsertionIndex(null);
    setIndicatorX(null);
  }, [stopAutoScroll]);

  // A drag can outlive the component (navigation mid-gesture); cancel the frame
  // loop rather than leave it scrolling a detached node.
  useEffect(() => stopAutoScroll, [stopAutoScroll]);

  const emitOrder = useCallback(
    (nextIds: string[]) => {
      const currentIds = tabs.map((t) => t.id);
      if (isSameOrder(currentIds, nextIds)) return;
      onTabReorder?.(nextIds);
    },
    [tabs, onTabReorder],
  );

  const handleDragStart = useCallback(
    (tab: EditorTab, event: React.DragEvent<HTMLDivElement>) => {
      if (!canReorder) {
        event.preventDefault();
        return;
      }
      draggingTabIdRef.current = tab.id;
      setDraggingTabId(tab.id);
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData(TAB_DRAG_MIME, tab.id);
      // Firefox refuses to start a drag without a text/plain payload.
      event.dataTransfer.setData("text/plain", tab.fileName ?? tab.label);
      startAutoScroll();
    },
    [canReorder, startAutoScroll],
  );

  /** Accept the drag and place the indicator. Bound to dragenter AND dragover. */
  const handleStripDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      if (!draggingTabIdRef.current) return; // Not our drag — let the browser have it.
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      pointerXRef.current = event.clientX;
      updateInsertion(event.clientX);
    },
    [updateInsertion],
  );

  const handleStripDragLeave = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      // `dragleave` also fires when crossing between tabs inside the strip; only a
      // departure from the strip itself counts. With one drop zone this is the
      // only leave that can happen at the strip level, but the check keeps the
      // indicator honest if a child ever starts accepting drops.
      const next = event.relatedTarget as Node | null;
      if (next && event.currentTarget.contains(next)) return;
      // Leave the drag alive (the pointer may come back) — just stop pointing at a
      // gap, so nothing is promised while the pointer is elsewhere.
      setInsertionIndex(null);
      setIndicatorX(null);
    },
    [],
  );

  const handleStripDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      const dragged = draggingTabIdRef.current;
      if (!dragged) return;
      event.preventDefault();

      // Recomputed from the drop's own coordinates rather than trusting the last
      // dragover: the drop is the commit, and it should honour where the pointer
      // actually was when the user let go.
      const measured = measureTabs();
      if (measured) {
        const index = resolveInsertionIndex(
          measured.bounds,
          dragged,
          event.clientX - measured.rowLeft,
        );
        emitOrder(insertTabIdAt(tabs.map((t) => t.id), dragged, index));
      }

      endDrag();
    },
    [emitOrder, endDrag, measureTabs, tabs],
  );

  const focusTab = useCallback((tabId: string) => {
    stripRef.current
      ?.querySelector<HTMLElement>(`[data-tab-id="${cssEscape(tabId)}"]`)
      ?.focus();
  }, []);

  const handleKeyDown = useCallback(
    (tab: EditorTab, event: React.KeyboardEvent<HTMLDivElement>) => {
      const index = tabs.findIndex((t) => t.id === tab.id);
      if (index === -1) return;

      // Alt+Arrow moves the tab; plain Arrow moves the selection. Alt is checked
      // first so the two never both fire.
      if (event.altKey && (event.key === "ArrowLeft" || event.key === "ArrowRight")) {
        if (!canReorder) return;
        event.preventDefault();
        emitOrder(
          shiftTabId(
            tabs.map((t) => t.id),
            tab.id,
            event.key === "ArrowLeft" ? -1 : 1,
          ),
        );
        // The element is keyed by tab id, so React moves the node rather than
        // remounting it and focus follows — but re-assert it for the case where
        // the parent rebuilds the list from scratch.
        requestAnimationFrame(() => focusTab(tab.id));
        return;
      }

      let nextIndex: number | null = null;
      switch (event.key) {
        case "ArrowLeft":
          nextIndex = Math.max(0, index - 1);
          break;
        case "ArrowRight":
          nextIndex = Math.min(tabs.length - 1, index + 1);
          break;
        case "Home":
          nextIndex = 0;
          break;
        case "End":
          nextIndex = tabs.length - 1;
          break;
        case "Enter":
        case " ":
          event.preventDefault();
          onTabClick?.(tab);
          return;
        default:
          return;
      }

      if (nextIndex === null || nextIndex === index) return;
      event.preventDefault();
      const nextTab = tabs[nextIndex];
      // Automatic activation, as in the ARIA tabs pattern and in VS Code: moving
      // along the strip shows the file you land on.
      onTabClick?.(nextTab);
      focusTab(nextTab.id);
    },
    [tabs, canReorder, emitOrder, focusTab, onTabClick],
  );

  if (tabs.length === 0) {
    return null;
  }

  return (
    <div
      ref={stripRef}
      role="tablist"
      aria-label="Open editors"
      aria-orientation="horizontal"
      className={cn(
        "flex min-w-0 w-full border-b border-border bg-muted/30 overflow-y-hidden",
        TAB_STRIP_SCROLLBAR,
        hasHorizontalOverflow ? "overflow-x-scroll" : "overflow-x-auto",
      )}
      // One drop zone for the whole strip. Both enter and over must be cancelled
      // for the element to stay a valid drop target.
      onDragEnter={handleStripDragOver}
      onDragOver={handleStripDragOver}
      onDragLeave={handleStripDragLeave}
      onDrop={handleStripDrop}
      onDragEnd={endDrag}
    >
      {/*
        The scrolled content, and the positioning context for the indicator.
        `w-max` lets the row grow past the viewport so tabs keep their natural
        width and the strip scrolls; `min-w-full` keeps the row spanning the strip
        when there are few tabs, so the bottom border does not stop short.
      */}
      <div
        ref={rowRef}
        className="relative flex w-max min-w-full flex-nowrap items-stretch"
      >
        {tabs.map((tab) => (
          <EditorTabItem
            key={tab.id}
            tab={tab}
            isActive={tab.id === activeTabId}
            isDragging={tab.id === draggingTabId}
            isDraggable={canReorder}
            onTabClick={onTabClick}
            onTabClose={onTabClose}
            onTabDoubleClick={onTabDoubleClick}
            onTabContextMenu={onTabContextMenu}
            onDragStart={handleDragStart}
            onKeyDown={handleKeyDown}
          />
        ))}

        {/*
          The insertion indicator: one element, positioned in content coordinates,
          for the lifetime of the drag. `-translate-x-1/2` centres it on the seam
          so it reads as "between these two tabs" rather than as part of either.
        */}
        {insertionIndex !== null && indicatorX !== null && (
          <div
            data-drop-indicator
            data-insertion-index={insertionIndex}
            aria-hidden="true"
            className="pointer-events-none absolute top-0 bottom-0 z-10 w-[2px] rounded-full bg-primary"
            style={{ left: `${indicatorX}px` }}
          />
        )}
      </div>
    </div>
  );
}

// ── Internal: Single Tab Item ──

interface EditorTabItemProps {
  tab: EditorTab;
  isActive: boolean;
  isDragging: boolean;
  isDraggable: boolean;
  onTabClick?: (tab: EditorTab) => void;
  onTabClose?: (tab: EditorTab) => void;
  onTabDoubleClick?: (tab: EditorTab) => void;
  onTabContextMenu?: (tab: EditorTab, event: React.MouseEvent) => void;
  onDragStart: (tab: EditorTab, event: React.DragEvent<HTMLDivElement>) => void;
  onKeyDown: (tab: EditorTab, event: React.KeyboardEvent<HTMLDivElement>) => void;
}

function EditorTabItem({
  tab,
  isActive,
  isDragging,
  isDraggable,
  onTabClick,
  onTabClose,
  onTabDoubleClick,
  onTabContextMenu,
  onDragStart,
  onKeyDown,
}: EditorTabItemProps) {
  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    onTabClose?.(tab);
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    onTabContextMenu?.(tab, e);
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    onTabDoubleClick?.(tab);
  };

  const title = tab.fileName ?? tab.label;

  return (
    <div
      data-tab-id={tab.id}
      role="tab"
      aria-selected={isActive}
      // Roving tabindex: one stop for the whole strip, then arrows within it.
      tabIndex={isActive ? 0 : -1}
      title={title}
      draggable={isDraggable}
      className={cn(
        "relative group flex shrink-0 items-center gap-1.5 px-3 py-1 text-xs border-r border-border cursor-pointer transition-colors select-none",
        "hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-inset",
        isActive ? "bg-background" : "bg-muted/50",
        // The tab being carried recedes; the indicator shows where it will land.
        // Opacity only — nothing that changes layout, or the geometry the
        // insertion index is measured from would shift mid-drag.
        isDragging && "opacity-40",
      )}
      onClick={() => onTabClick?.(tab)}
      onDoubleClick={handleDoubleClick}
      onContextMenu={handleContextMenu}
      onDragStart={(e) => onDragStart(tab, e)}
      onKeyDown={(e) => onKeyDown(tab, e)}
    >
      {/* Active underline — narrow centered indicator */}
      {isActive && (
        <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-2/3 h-[2px] rounded-full bg-primary" />
      )}

      {/* Pin indicator */}
      {tab.isPinned && (
        <Pin className="h-3 w-3 shrink-0 text-muted-foreground" />
      )}

      {/* Custom icon from parent */}
      {tab.icon && (
        <span className="shrink-0 flex items-center">{tab.icon}</span>
      )}

      {/* Tab label — italic for preview tabs, bold for dirty */}
      <span
        className={cn(
          "max-w-[180px] truncate",
          tab.isPreview && "italic text-muted-foreground",
          tab.isDirty && !tab.isPreview && "font-medium",
        )}
      >
        {tab.label}
      </span>

      {/* Dirty indicator or close button */}
      {onTabClose && (
        <button
          type="button"
          aria-label={`Close ${title}`}
          // Excluded from the drag: pressing close is a click, not a pick-up.
          draggable={false}
          className={cn(
            "h-3.5 w-3.5 rounded flex items-center justify-center transition-colors shrink-0",
            "hover:bg-accent",
            "opacity-0 group-hover:opacity-100 focus-visible:opacity-100",
            tab.isDirty && "opacity-100",
          )}
          onClick={handleClose}
        >
          {tab.isDirty ? (
            <Circle className="h-2 w-2 fill-current" />
          ) : (
            <X className="h-3 w-3" />
          )}
        </button>
      )}
    </div>
  );
}

/**
 * Minimal CSS.escape for attribute selectors.
 *
 * Tab ids are UUIDs today, but they are minted by the parent — a metadata id with
 * a dot or colon in it would otherwise turn a lookup into a silent miss.
 */
function cssEscape(value: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(value);
  }
  return value.replace(/["\\]/g, "\\$&");
}
